import { clientWithApiClient } from "../../client";
import { ApiClient } from "../../types";
import { ListResponse, Response } from "../../types";
import {
  InsuranceOption,
  CreateInsuranceOptionRequest,
  UpdateInsuranceOptionRequest,
  CreateInsuranceDocumentRequest,
  InsuranceChecks,
  OfferedInsuranceOption,
  SetInsuranceVehicleRequest,
  VehicleInsuranceScopeItem,
} from "./types";

const BASE_PATH = "/insurance-options";

export const insuranceOptions = {
  list: async (client: ApiClient, fleetId: string) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}?${queryString}`;

    return clientWithApiClient<ListResponse<InsuranceOption>>(client, url);
  },

  /**
   * The options one unit offers, at the unit's effective amounts. The fleet
   * comes from the API key; pass `fleetId` for user sessions.
   */
  listForVehicle: async (
    client: ApiClient,
    vehicleId: string,
    fleetId?: string,
  ) => {
    const searchParams = new URLSearchParams();
    if (fleetId) searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `/vehicles/${vehicleId}/insurance-options${queryString ? `?${queryString}` : ""}`;

    return clientWithApiClient<ListResponse<OfferedInsuranceOption>>(
      client,
      url,
    );
  },

  /** The options one category offers, at the effective amounts. */
  listForCategory: async (
    client: ApiClient,
    categoryId: string,
    fleetId: string,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const url = `/categories/${categoryId}/insurance-options?${searchParams.toString()}`;

    return clientWithApiClient<ListResponse<OfferedInsuranceOption>>(
      client,
      url,
    );
  },

  /** What the fleet's insurance leaves uncovered or on the package price. */
  checks: async (client: ApiClient, fleetId: string) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const url = `${BASE_PATH}/checks?${searchParams.toString()}`;

    return clientWithApiClient<Response<InsuranceChecks>>(client, url);
  },

  get: async (client: ApiClient, fleetId: string, id: string) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${id}?${queryString}`;

    return clientWithApiClient<Response<InsuranceOption>>(client, url);
  },

  create: async (
    client: ApiClient,
    fleetId: string,
    data: CreateInsuranceOptionRequest,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}?${queryString}`;

    return clientWithApiClient<Response<InsuranceOption>>(client, url, {
      method: "POST",
      body: JSON.stringify({ insurance_option: data }),
    });
  },

  update: async (
    client: ApiClient,
    fleetId: string,
    id: string,
    data: UpdateInsuranceOptionRequest,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${id}?${queryString}`;

    return clientWithApiClient<Response<InsuranceOption>>(client, url, {
      method: "PATCH", // Using PATCH as per addons pattern
      body: JSON.stringify({ insurance_option: data }),
    });
  },

  delete: async (client: ApiClient, fleetId: string, id: string) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${id}?${queryString}`;

    return clientWithApiClient(client, url, {
      method: "DELETE",
    });
  },

  /**
   * Restores a deleted option. If its group was deleted meanwhile, it comes
   * back ungrouped.
   */
  restore: async (client: ApiClient, fleetId: string, id: string) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${id}/restore?${queryString}`;

    return clientWithApiClient<Response<InsuranceOption>>(client, url, {
      method: "POST",
    });
  },

  createDocument: async (
    client: ApiClient,
    fleetId: string,
    optionId: string,
    data: CreateInsuranceDocumentRequest | FormData,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${optionId}/documents?${queryString}`;

    const body =
      data instanceof FormData ? data : JSON.stringify({ document: data });

    return clientWithApiClient(client, url, {
      method: "POST",
      body,
    });
  },

  deleteDocument: async (
    client: ApiClient,
    fleetId: string,
    optionId: string,
    documentId: string,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const queryString = searchParams.toString();
    const url = `${BASE_PATH}/${optionId}/documents/${documentId}?${queryString}`;

    return clientWithApiClient(client, url, {
      method: "DELETE",
    });
  },

  /**
   * Every active option of the fleet on one unit: whether it is offered, why,
   * the effective amounts and the unit's own.
   */
  vehicleScope: async (
    client: ApiClient,
    fleetId: string,
    vehicleId: string,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const url = `/vehicles/${vehicleId}/insurance-scope?${searchParams.toString()}`;

    return clientWithApiClient<ListResponse<VehicleInsuranceScopeItem>>(
      client,
      url,
    );
  },

  /** Offers the option on one vehicle, or sets that unit's own amounts. */
  setForVehicle: async (
    client: ApiClient,
    fleetId: string,
    vehicleId: string,
    optionId: string,
    attrs: SetInsuranceVehicleRequest,
  ) => {
    const searchParams = new URLSearchParams();
    searchParams.append("fleet_id", fleetId);
    const url = `/vehicles/${vehicleId}/insurance-options/${optionId}?${searchParams.toString()}`;

    return clientWithApiClient(client, url, {
      method: "PUT",
      body: JSON.stringify(attrs),
    });
  },
};
